/**
 * Vocabulary: readers see this feature as "owner reviews"; code, routes, DealHunt paths and
 * analytics events keep the historical "owner note" name on purpose — do not rename them.
 *
 * The declared product's comparison numbers, in the v3 card's shape, from the product-line
 * payload the band already resolves for a typeahead pick outside the grid (contract #63;
 * ticket §B). Prefers DealHunt's `compare` object (one resolver for both surfaces); until it
 * ships, derives the same fields from `ratingsBySource`, `specs` and `clearbuyProductId`.
 * Pure; tested in `tests/owner-compare-source.spec.ts`.
 */
import type {
  DealHuntCategoryHit,
  DealHuntCompareSpec,
  DealHuntProductLine,
} from 'lib/dealhunt/types'
import { SCORE_AXES, type CompareSpec, type DeltaNumbers } from './deltas'

/** Rating type → its axis's filter scale (`SCORE_AXES`: MDAQS /5, else /10 — mirrors
 *  `latestAxisScoresSql`'s sources). A rating type not listed is not an axis. */
const AXIS_SCALE: ReadonlyMap<string, number> = new Map(
  SCORE_AXES.flatMap(axis => axis.keys.map(key => [key, axis.max] as const))
)

/** The card fold's keys first, then the wireless-hours key most audio lines actually carry
 *  (the category filter reads it; DealHunt's `battery_hours` fold does not yet — ticket §A),
 *  then the variants a line lists when it has no headline figure. ANC-on is last: it is the
 *  shorter of a line's two figures (WH-1000XM5 40 h, 30 h with ANC). Never the earbuds' case
 *  total (`battery_life_with_case`): that is not one listening session. */
const BATTERY_KEYS = [
  'battery.battery_life',
  'battery.playtime_h',
  'battery.battery_life_wireless',
  'battery.battery_life_hours',
  'battery.battery_life_typical',
  'battery.life',
  'battery.battery_life_anc_on',
]

function toCompareSpecs(specs: DealHuntCompareSpec[] | null | undefined): CompareSpec[] | null {
  if (!specs?.length) {
    return null
  }
  return specs.map(s => ({
    key: s.key,
    label: s.label,
    value: s.value,
    unit: s.unit,
    dataType: s.dataType,
    values: Array.isArray(s.values) ? s.values : null,
  }))
}

/** A v3 card's numbers (the grid side, and the declared side when the pick is in the grid). */
export function numbersFromHit(hit: DealHuntCategoryHit): DeltaNumbers {
  return {
    axes: hit.axes ?? null,
    batteryHours: hit.batteryHours ?? null,
    priceCents: hit.currentPriceCents,
    sgScore: hit.sgReviewScore ?? null,
    clearbuyProductId: hit.clearbuyProductId ?? null,
    compareSpecs: toCompareSpecs(hit.compareSpecs),
  }
}

/** Battery hours from the PDP spec list — the same keys as the card's `battery_hours` fold. */
function batteryFromSpecs(specs: DealHuntProductLine['enrichment']['specs']): number | null {
  for (const key of BATTERY_KEYS) {
    const spec = specs.find(s => s.specKey === key)
    if (spec) {
      const n = Number(String(spec.specValue).replace(/[^0-9.]/g, ''))
      if (Number.isFinite(n) && n > 0) {
        return n
      }
    }
  }
  return null
}

/**
 * The declared product's numbers from its product-line payload. `page.compare` wins when
 * DealHunt ships it; the derivation below reads the SoundGuys ratings group on the axis's
 * filter scale (rescaled when the source scale differs), the battery spec and the lab id.
 * Price/SG score come from the typeahead hit, so they stay as passed.
 */
export function numbersFromProductLine(
  page: DealHuntProductLine,
  base: Pick<DeltaNumbers, 'priceCents' | 'sgScore'>
): DeltaNumbers {
  const compare = page.compare ?? null
  if (compare) {
    return {
      ...base,
      axes: compare.axes ?? null,
      batteryHours: compare.batteryHours ?? batteryFromSpecs(page.enrichment.specs ?? []),
      clearbuyProductId: compare.clearbuyProductId ?? page.clearbuyProductId ?? null,
      compareSpecs: toCompareSpecs(compare.compareSpecs),
    }
  }
  const group = page.enrichment.ratingsBySource.find(g => /soundguys/i.test(g.sourceName ?? ''))
  let axes: Record<string, number> | null = null
  let { sgScore } = base
  if (group) {
    axes = {}
    for (const r of group.ratings) {
      const scale = AXIS_SCALE.get(r.ratingType)
      if (scale === undefined || !r.ratingScale) {
        continue
      }
      const onScale =
        r.ratingScale === scale ? r.ratingValue : (r.ratingValue / r.ratingScale) * scale
      axes[r.ratingType] = Math.round(onScale * 10) / 10
    }
    if (!Object.keys(axes).length) {
      axes = null
    }
    const overall = group.ratings.find(r => r.ratingType === 'overall')
    if (overall?.ratingScale && sgScore === null) {
      sgScore = Math.round((overall.ratingValue / overall.ratingScale) * 100) / 10
    }
  }
  return {
    priceCents: base.priceCents,
    sgScore,
    axes,
    batteryHours: batteryFromSpecs(page.enrichment.specs ?? []),
    clearbuyProductId: page.clearbuyProductId ?? null,
    compareSpecs: null,
  }
}
